// src/components/Uploader.js
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

/* Brand tokens */
const DEEP_BLUE = "#1b2a41";
const CORAL_RED = "#ff6b6b";
const LIGHT_BG = "#f7f7f7";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

/* Tiny UI helpers */
const StepTitle = ({ number, children }) => (
  <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
    <div style={{
      width: 36, height: 36, borderRadius: 10, background: DEEP_BLUE,
      color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
      fontWeight: 700, marginRight: 12
    }}>{number}</div>
    <h3 style={{ margin: 0, color: DEEP_BLUE, fontSize: 18 }}>{children}</h3>
  </div>
);

const CTAButton = ({ children, onClick, disabled, style = {} }) => (
  <button onClick={onClick} disabled={disabled}
    style={{
      border: "none", padding: "10px 16px", borderRadius: 12,
      cursor: disabled ? "not-allowed" : "pointer", fontWeight: 700,
      background: `linear-gradient(135deg, ${DEEP_BLUE} 0%, ${CORAL_RED} 100%)`,
      color: "#fff", boxShadow: "0 8px 30px rgba(27,42,65,0.08)", ...style
    }}>
    {children}
  </button>
);

const OutlineButton = ({ children, onClick, disabled, style = {} }) => (
  <button onClick={onClick} disabled={disabled}
    style={{
      border: `1.5px solid ${DEEP_BLUE}`, padding: "8px 14px", borderRadius: 12,
      cursor: disabled ? "not-allowed" : "pointer", fontWeight: 600, background: "#fff",
      color: DEEP_BLUE, ...style
    }}>
    {children}
  </button>
);

export default function Uploader() {
  const [mode, setMode] = useState("drive");

  /* Drive states */
  const [isSourceConnected, setIsSourceConnected] = useState(false);
  const [isDestinationConnected, setIsDestinationConnected] = useState(false);
  const [gdriveDestination, setGdriveDestination] = useState("gdrive-source");
  const [gdriveFolders, setGdriveFolders] = useState([]);
  const [isFetchingFolders, setIsFetchingFolders] = useState(false);
  const [isProcessingFolder, setIsProcessingFolder] = useState(false);
  const [gdriveMessage, setGdriveMessage] = useState("");
  const [gdriveResults, setGdriveResults] = useState({});

  /* Local states */
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [localDestination, setLocalDestination] = useState("local");
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const [localMessage, setLocalMessage] = useState("");
  const [localResults, setLocalResults] = useState({});

  useEffect(() => {
    checkConnectionStatus();
    if (!document.getElementById("montserrat-font")) {
      const link = document.createElement("link");
      link.id = "montserrat-font";
      link.href = "https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&display=swap";
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
  }, []);

  async function checkConnectionStatus() {
    try {
      const res = await axios.get(`${API_BASE_URL}/auth/gdrive/status`, { withCredentials: true });
      setIsSourceConnected(Boolean(res.data?.source_connected));
      setIsDestinationConnected(Boolean(res.data?.destination_connected));
    } catch {
      setIsSourceConnected(false);
      setIsDestinationConnected(false);
    }
  }

  /* Drive actions */
  async function fetchGdriveFolders() {
    setGdriveFolders([]); setGdriveMessage(""); setIsFetchingFolders(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/auth/gdrive/files`, { withCredentials: true });
      setGdriveFolders(res.data || []);
      setGdriveMessage(res.data && res.data.length > 0 ? "Select a folder to process." : "No folders found in root.");
    } catch (err) {
      setGdriveMessage(err?.response?.status === 401 ? "Error: Source Account disconnected." : "Failed to fetch folders.");
    } finally {
      setIsFetchingFolders(false);
    }
  }

  async function handleProcessFolder(folderId, folderName) {
    setIsProcessingFolder(true); setGdriveMessage(`Processing folder: "${folderName}"...`); setGdriveResults({});
    try {
      const res = await axios.post(`${API_BASE_URL}/auth/gdrive/process-folder/${folderId}`, { destination: gdriveDestination }, { withCredentials: true });
      let successMessage = res.data?.message || "Processing complete.";
      if (gdriveDestination !== "local" && !successMessage.includes("Output")) successMessage += " Results saved in the destination Drive 'Output' folder.";
      setGdriveMessage(successMessage); setGdriveResults(res.data?.results || {});
    } catch (err) {
      setGdriveMessage(err?.response?.status === 401 && gdriveDestination === "gdrive-destination" ? "Error: Destination not connected." : "Error processing folder.");
    } finally {
      setIsProcessingFolder(false);
    }
  }

  /* Local actions */
  function handleFileChange(e) {
    const selected = Array.from(e.target.files || []);
    setFiles(selected); setLocalMessage(""); setLocalResults({});
  }

  const handleLocalUpload = async () => {
    if (files.length === 0) { setLocalMessage("Error: Please select files or a folder first!"); return; }
    setIsLocalLoading(true);
    setLocalMessage(`Processing ${files.length} item(s)...`);

    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    formData.append('destination', localDestination);

    try {
      if (localDestination === 'local') {
        const response = await axios.post(`${API_BASE_URL}/process-upload`, formData, {
          withCredentials: true,
          responseType: 'blob',
        });

        // Determine filename from header; fallback to timestamped name
        let filename = null;
        const contentDisp = response.headers && response.headers['content-disposition'];
        if (contentDisp) {
          const m = contentDisp.match(/filename\*?=(?:UTF-8'')?\"?([^;"]+)\"?/);
          if (m && m[1]) filename = decodeURIComponent(m[1]);
        }
        if (!filename) {
          const now = new Date();
          const ts = now.toISOString().replace(/[:.]/g, '-');
          filename = `PixClad_Output_${ts}.zip`;
        }

        const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/zip' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = filename;
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(url);

        setLocalMessage('Download started — your categorized files are ready.');
        setLocalResults({});

      } else {
        // Save to Drive (existing flow)
        const res = await axios.post(`${API_BASE_URL}/process-upload`, formData, { withCredentials: true });
        setLocalResults(res.data.results || {}); setLocalMessage('Processing complete! Files saved to Google Drive.');
      }
    } catch (error) {
      // Try to parse JSON error blob if available
      if (error.response && error.response.data) {
        try {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const json = JSON.parse(reader.result);
              setLocalMessage(json.error || 'Error: Could not process the upload.');
            } catch {
              setLocalMessage('Error: Could not process the upload.');
            }
          };
          reader.readAsText(error.response.data);
        } catch {
          setLocalMessage('Error: Could not process the upload.');
        }
      } else {
        setLocalMessage('Error: Could not process the upload.');
      }
    } finally {
      setIsLocalLoading(false);
    }
  };

  const resetDriveState = () => { setGdriveFolders([]); setGdriveMessage(""); setGdriveResults({}); };

  const styles = {
    page: { minHeight: "100vh", background: LIGHT_BG, fontFamily: "'Montserrat', sans-serif", padding: "40px 20px", color: DEEP_BLUE },
    container: { maxWidth: 980, margin: "0 auto" },
    card: { background: "#fff", borderRadius: 16, boxShadow: "0 12px 40px rgba(11,18,30,0.06)", padding: 22 },
    headerRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 },
    subtitle: { margin: "6px 0 0 0", color: "#6b7280", fontSize: 14 },
    modeSelector: { display: "flex", gap: 10, alignItems: "center" },
    grid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginTop: 18 },
    panel: { background: "#fff", borderRadius: 12, padding: 18, border: `1px solid rgba(0,0,0,0.04)` },
    field: { marginTop: 12, display: "flex", flexDirection: "column", gap: 8 },
    select: { padding: "10px 12px", borderRadius: 8, border: `1px solid rgba(0,0,0,0.08)`, background: "#fff" },
    listItem: { padding: 12, borderRadius: 8, border: `1px solid rgba(0,0,0,0.04)`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 },
    resultBox: { marginTop: 14, padding: 12, borderRadius: 8, background: "#fff", border: `1px solid #eee` },
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.headerRow}>
            <div>
              <h2 style={{ margin: 0, color: DEEP_BLUE, fontSize: 22 }}>PixClad — Folder Categorization</h2>
              <p style={styles.subtitle}>Connect a source account or upload files from your computer.</p>
            </div>

            <div style={styles.modeSelector}>
              <button onClick={() => { setMode("drive"); resetDriveState(); }} style={{
                padding: "10px 16px", borderRadius: 12, fontWeight: 700,
                border: mode === "drive" ? `2px solid ${CORAL_RED}` : "1px solid rgba(0,0,0,0.06)",
                background: mode === "drive" ? DEEP_BLUE : "#fff",
                color: mode === "drive" ? "#fff" : DEEP_BLUE, cursor: "pointer"
              }}>Google Drive</button>

              <button onClick={() => setMode("local")} style={{
                padding: "10px 16px", borderRadius: 12, fontWeight: 700,
                border: mode === "local" ? `2px solid ${CORAL_RED}` : "1px solid rgba(0,0,0,0.06)",
                background: mode === "local" ? DEEP_BLUE : "#fff",
                color: mode === "local" ? "#fff" : DEEP_BLUE, cursor: "pointer"
              }}>Upload from Computer</button>
            </div>
          </div>

          <div style={{ marginTop: 10 }}>
            {mode === "drive" ? (
              <div style={styles.grid}>
                <div style={styles.panel}>
                  <StepTitle number="1">Connect Source</StepTitle>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      <div style={{ fontSize: 14, color: "#6b7280" }}>Link the Google account which contains the folders you want to process.</div>
                      <div style={{ marginTop: 8 }}>
                        <a href={`${API_BASE_URL}/auth/gdrive/login`} style={{ textDecoration: "none" }}>
                          <CTAButton>Connect Source Account</CTAButton>
                        </a>
                      </div>
                    </div>
                  </div>

                  <div style={{ marginTop: 14 }}>
                    {isSourceConnected ? (
                      <div style={{ padding: 10, borderRadius: 8, background: "#e9f7ef", color: DEEP_BLUE, fontWeight: 600 }}>✅ Source Account Connected</div>
                    ) : (
                      <div style={{ padding: 10, borderRadius: 8, background: "#fff6f6", color: CORAL_RED }}>⚠ Source not connected</div>
                    )}
                  </div>

                  <div style={styles.field}>
                    <StepTitle number="2">Output Destination</StepTitle>
                    <div style={{ fontSize: 13, color: "#374151", fontWeight: 600 }}>Where should processed files be saved?</div>
                    <select value={gdriveDestination} onChange={(e) => setGdriveDestination(e.target.value)} style={styles.select}>
                      <option value="gdrive-source">Same Drive ('Output' folder)</option>
                      <option value="gdrive-destination">Different Drive ('Output' folder)</option>
                      <option value="local">Download Locally</option>
                    </select>

                    {gdriveDestination === "gdrive-destination" && (
                      <div style={{ marginTop: 12 }}>
                        {!isDestinationConnected ? (
                          <a href={`${API_BASE_URL}/auth/gdrive/login-destination`} style={{ textDecoration: "none" }}>
                            <OutlineButton>Connect Destination Drive</OutlineButton>
                          </a>
                        ) : (
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                            <div style={{ padding: 10, borderRadius: 8, background: "#e9f7ef", color: DEEP_BLUE }}>✅ Destination Drive Connected</div>
                            <a href={`${API_BASE_URL}/auth/gdrive/logout-destination`} style={{ textDecoration: "none" }}>
                              <button style={{ background: "transparent", border: "none", color: CORAL_RED, cursor: "pointer", fontWeight: 700 }}>Change</button>
                            </a>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div style={styles.field}>
                    <StepTitle number="3">Fetch Folders</StepTitle>
                    <div style={{ fontSize: 13, color: "#6b7280" }}>Load folders from your source Drive to pick one to process.</div>
                    <div style={{ marginTop: 8 }}>
                      <OutlineButton onClick={fetchGdriveFolders} disabled={!isSourceConnected || isFetchingFolders}>{isFetchingFolders ? "Fetching..." : "Fetch Folders"}</OutlineButton>
                    </div>
                    {isFetchingFolders && <div style={{ marginTop: 8 }}><progress style={{ width: "100%" }} /></div>}
                  </div>

                  <div style={styles.field}>
                    <StepTitle number="4">Select a Folder to Process</StepTitle>
                    <div style={{ marginTop: 8 }}>
                      {gdriveFolders.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          {gdriveFolders.map((f, i) => (
                            <div key={f.id || i} role="button" onClick={() => handleProcessFolder(f.id, f.name)} style={{ ...styles.listItem, cursor: isProcessingFolder ? "not-allowed" : "pointer", borderLeft: `6px solid ${CORAL_RED}`, opacity: isProcessingFolder ? 0.6 : 1 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                <div style={{ fontWeight: 700 }}>{f.name}</div>
                                <div style={{ fontSize: 13, color: "#6b7280" }}>{f.mimeType || ""}</div>
                              </div>
                              <div style={{ fontSize: 13, color: "#6b7280" }}></div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ padding: 12, borderRadius: 8, background: "#fff" }}>
                          <div style={{ color: "#6b7280" }}>{gdriveMessage || "No folders loaded yet."}</div>
                          <div style={{ marginTop: 10 }}><OutlineButton onClick={fetchGdriveFolders}>Try Again</OutlineButton></div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ marginTop: 16 }}>
                    {gdriveMessage && <div style={{ padding: 12, borderRadius: 8, background: "#fff" }}><strong>{gdriveMessage}</strong></div>}
                    {Object.keys(gdriveResults).length > 0 && (
                      <div style={styles.resultBox}><div style={{ fontWeight: 700, marginBottom: 8, color: DEEP_BLUE }}>Processing Summary</div>
                        <div style={{ display: "grid", gap: 8 }}>
                          {Object.entries(gdriveResults).map(([name, tags]) => (<div key={name} style={{ display: "flex", justifyContent: "space-between" }}><div>{name}</div><div style={{ color: CORAL_RED }}>{tags?.[0] || "Uncategorized"}</div></div>))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div style={styles.panel}>
                  <div style={{ marginBottom: 12 }}>
                    <h4 style={{ margin: 0, color: DEEP_BLUE }}>Quick Tips</h4>
                    <div style={{ color: "#6b7280", marginTop: 8 }}>
                      • Connect source to list folders. <br />
                      • Choose output destination before processing. <br />
                      • If using different Drive, connect destination account.
                    </div>
                  </div>

                  <div style={{ marginTop: 18 }}>
                    <h4 style={{ margin: 0, color: DEEP_BLUE }}>Status</h4>
                    <div style={{ marginTop: 12 }}>
                      <div style={{ marginBottom: 8 }}><strong>Source:</strong> {isSourceConnected ? "Connected" : "Not connected"}</div>
                      <div><strong>Destination:</strong> {gdriveDestination === "gdrive-destination" ? (isDestinationConnected ? "Connected" : "Not connected") : "Using selected option"}</div>
                    </div>
                  </div>

                  <div style={{ marginTop: 20 }}>
                    <h4 style={{ margin: 0, color: DEEP_BLUE }}>Need help?</h4>
                    <div style={{ color: "#6b7280", marginTop: 8 }}>
                      If you experience connection issues, please try reconnecting your Google account or contact support.
                    </div>
                    <div style={{ marginTop: 12 }}><a href={`${API_BASE_URL}/auth/gdrive/status`} style={{ color: DEEP_BLUE, textDecoration: "underline" }}>Refresh status</a></div>
                  </div>
                </div>
              </div>
            ) : (
              <div style={styles.grid}>
                <div style={{ ...styles.panel }}>
                  <StepTitle number="1">Select Output Location</StepTitle>
                  <div style={{ color: "#6b7280", marginBottom: 8 }}>Choose where processed files should be saved.</div>
                  <div><select value={localDestination} onChange={(e) => setLocalDestination(e.target.value)} style={styles.select}><option value="local">Download Locally</option><option value="gdrive">Save to Google Drive</option></select></div>

                  <div style={styles.field}>
                    <StepTitle number="2">Select Files or Folder</StepTitle>
                    <div style={{ color: "#6b7280" }}>Upload individual files or a full folder.</div>
                    <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                      <input type="file" ref={fileInputRef} onChange={handleFileChange} multiple style={{ display: "none" }} />
                      <input type="file" ref={folderInputRef} onChange={handleFileChange} webkitdirectory="true" directory="true" style={{ display: "none" }} />
                      <OutlineButton onClick={() => fileInputRef.current.click()}>Select File(s)</OutlineButton>
                      <OutlineButton onClick={() => folderInputRef.current.click()}>Select Folder</OutlineButton>
                    </div>
                    <div style={{ marginTop: 10, color: "#374151" }}>{files.length} item(s) selected.</div>
                  </div>

                  <div style={styles.field}>
                    <StepTitle number="3">Process Upload</StepTitle>
                    <div style={{ color: "#6b7280" }}>Start categorization for selected files.</div>
                    <div style={{ marginTop: 12 }}><CTAButton onClick={handleLocalUpload} disabled={isLocalLoading || files.length === 0}>{isLocalLoading ? "Processing..." : "Process Upload"}</CTAButton></div>
                    {isLocalLoading && <div style={{ marginTop: 8 }}><progress style={{ width: "100%" }} /></div>}
                  </div>

                  <div style={{ marginTop: 16 }}>
                    {localMessage && <div style={{ padding: 10, borderRadius: 8, background: "#fff" }}>{localMessage}</div>}
                    {Object.keys(localResults).length > 0 && (
                      <div style={styles.resultBox}><div style={{ fontWeight: 700, marginBottom: 8, color: DEEP_BLUE }}>Local Upload Summary</div>
                        <div style={{ display: "grid", gap: 8 }}>
                          {Object.entries(localResults).map(([name, tags]) => (<div key={name} style={{ display: "flex", justifyContent: "space-between" }}><div>{name}</div><div style={{ color: CORAL_RED }}>{tags?.[0] || "Uncategorized"}</div></div>))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ ...styles.panel }}>
                  <h4 style={{ margin: 0, color: DEEP_BLUE }}>Local Upload Tips</h4>
                  <div style={{ color: "#6b7280", marginTop: 8 }}>• Select multiple files or a folder. <br />• Choose where processed files should go. <br />• If saving to Drive, ensure you're logged in below.</div>

                  {localDestination === "gdrive" && (
                    <div style={{ marginTop: 16 }}>{!isSourceConnected ? (<a href={`${API_BASE_URL}/auth/gdrive/login`} style={{ textDecoration: "none" }}><OutlineButton>Connect Google Drive</OutlineButton></a>) : (<div style={{ padding: 10, borderRadius: 8, background: "#e9f7ef", color: DEEP_BLUE }}>✅ Drive Connected (will save outputs to your Drive)</div>)}</div>
                  )}

                  <div style={{ marginTop: 14 }}>
                    <a href="#" onClick={() => { setFiles([]); setLocalResults({}); setLocalMessage(""); }} style={{ color: DEEP_BLUE }}>Clear selection</a>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}